import {
  AlertTriangle,
  Briefcase,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  UserCheck,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import type { NotificationType } from "@/api/query/useAllNotificationQuery";

export type TimesheetEmailRuleKey = "generated" | "reminder1" | "reminder2";

export type TimesheetEventKind = "approved" | "rejected" | "pending";

/**
 * Parses `ts_email:{organisationId}:{userId}:{ruleKey}:{periodYmd}` (period has no ':').
 */
export function parseTimesheetEmailRuleKey(
  referenceId: string | undefined,
): TimesheetEmailRuleKey | null {
  if (!referenceId?.startsWith("ts_email:")) return null;
  const parts = referenceId.split(":");
  if (parts.length < 5) return null;
  const key = parts[3];
  if (key === "generated" || key === "reminder1" || key === "reminder2") {
    return key;
  }
  return null;
}

/** `ts_event:approved|rejected|pending:...` */
export function parseTimesheetEventKind(
  referenceId: string | undefined,
): TimesheetEventKind | null {
  if (!referenceId?.startsWith("ts_event:")) return null;
  const parts = referenceId.split(":");
  const kind = parts[1];
  if (kind === "approved" || kind === "rejected" || kind === "pending") {
    return kind;
  }
  return null;
}

export function getNotificationClickPath(
  notification: Pick<NotificationType, "type" | "referenceId" | "projectId">,
): string {
  const { type, referenceId, projectId } = notification;
  if (type === "TIMESHEET") {
    const ev = parseTimesheetEventKind(referenceId);
    if (ev === "pending") return "/timesheet-approvals";
    if (ev === "approved" || ev === "rejected") return "/my-timesheet";
    if (parseTimesheetEmailRuleKey(referenceId)) return "/my-timesheet";
    return "/my-timesheet";
  }
  if (type === "TASK" && projectId) {
    return `/tasks/${projectId}?task=${referenceId}`;
  }
  if (type === "RISK" && projectId) {
    return `/risk/${projectId}?riskId=${referenceId}`;
  }
  if (type === "PROJECT") {
    return `/project-details/${referenceId}`;
  }
  return "/";
}

export function isNotificationDetailsTranslationKey(details: string | undefined): boolean {
  return Boolean(details?.startsWith("notification_body_"));
}

export type NotificationPresentation = {
  Icon: LucideIcon;
  circleClass: string;
  iconClass: string;
  titleKey: string;
};

export function getNotificationPresentation(
  notification: Pick<NotificationType, "type" | "referenceId">,
): NotificationPresentation {
  const { type, referenceId } = notification;

  if (type === "RISK") {
    return {
      Icon: AlertTriangle,
      circleClass: "bg-red-50",
      iconClass: "text-red-600",
      titleKey: "notification_title_risk",
    };
  }

  if (type === "TIMESHEET") {
    const ev = parseTimesheetEventKind(referenceId);
    if (ev === "approved") {
      return {
        Icon: CheckCircle2,
        circleClass: "bg-emerald-50",
        iconClass: "text-emerald-600",
        titleKey: "notification_title_timesheet_approved",
      };
    }
    if (ev === "rejected") {
      return {
        Icon: XCircle,
        circleClass: "bg-red-50",
        iconClass: "text-red-600",
        titleKey: "notification_title_timesheet_rejected",
      };
    }
    if (ev === "pending") {
      return {
        Icon: UserCheck,
        circleClass: "bg-indigo-50",
        iconClass: "text-indigo-600",
        titleKey: "notification_title_timesheet_pending_approval",
      };
    }

    const rk = parseTimesheetEmailRuleKey(referenceId);
    if (rk === "reminder1") {
      return {
        Icon: Clock,
        circleClass: "bg-amber-50",
        iconClass: "text-amber-600",
        titleKey: "notification_title_timesheet_reminder1",
      };
    }
    if (rk === "reminder2") {
      return {
        Icon: AlertTriangle,
        circleClass: "bg-red-50",
        iconClass: "text-red-600",
        titleKey: "notification_title_timesheet_reminder2",
      };
    }
    return {
      Icon: Calendar,
      circleClass: "bg-blue-50",
      iconClass: "text-blue-600",
      titleKey: "notification_title_timesheet_generated",
    };
  }

  if (type === "PROJECT") {
    return {
      Icon: Briefcase,
      circleClass: "bg-blue-50",
      iconClass: "text-blue-600",
      titleKey: "notification_title_project",
    };
  }

  return {
    Icon: ClipboardList,
    circleClass: "bg-slate-100",
    iconClass: "text-slate-600",
    titleKey: "notification_title_task",
  };
}
